import React, {useState, useEffect} from 'react';

import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import classnames from "classnames";
import {useDealContext} from "../../contexts/DealContext";
import {useUserContext} from "../../contexts/UserContext";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import Tooltip from "@mui/material/Tooltip";
import CustomCurrencyInput from "../../components/CustomCurrencyInput";
import {useMediaQuery} from "react-responsive";
import {mediaQueryForMobile} from "../HomePage/HomePage";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import {CustomSelect} from "../../components/CustomSelect";
import {DeskingModal} from "../PricingListPage/DeskingModal";
import {useModalsStore} from "../../stores/modalsStore";
import {CostCalculator} from "../../components/CostCalculator";
import {costUpdate} from "../../components/costCalculatorMath";
import Switch from "@mui/material/Switch";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Popover from "@mui/material/Popover";
import {NumericFormat} from 'react-number-format';
import '../../components/AmountDueBreakdownPopover.scss';


export const mileageOptions = [5000, 7500, 10000, 10500, 12000, 15000]

export const termOptions = ['18','24','27','30','33','36','39','42','45','48','60','72']

export const DealPagePayment = ({ isDealPage }) => {
    const isTabletOrMobile = useMediaQuery({ query: mediaQueryForMobile })
    const {
        dealState,
        dealColorLight,
        setDealStateWithUpdate,
        setDealStateWithoutUpdate,
        updateDealFunc,
        updateDealResponse,
        isHistorical,
    } = useDealContext()
    const { canDesk } = useUserContext()
    const openModal = useModalsStore((state) => state.openModal)
    const formattedQuote = dealState.formatted_quote
    const [paymentState, setPaymentState] = useState({
        desired_payment: dealState.desired_payment || formattedQuote.monthly_payment,
        is_drive_off_cap_reduction: dealState.is_drive_off_cap_reduction,
        drive_off: dealState.drive_off,
        _should_update: false
    })

    // ── Residual override field ──
    // The editable input shows the base residual (or the override once set). The
    // "+adjustment = total" readout to its right comes straight from the quote.
    const hasResidualOverride =
        dealState.residual_override !== undefined &&
        dealState.residual_override !== '' &&
        dealState.residual_override !== null
    const committedResidual = hasResidualOverride
        ? dealState.residual_override
        : (formattedQuote.base_residual ?? '')
    // Hold the typed value locally so typing doesn't update dealState on every
    // keystroke — that's what made the adjustment number (and the widget width)
    // jump around. Commit to the deal + recalc only on Enter or blur.
    const [residualInput, setResidualInput] = useState(String(committedResidual))

    // Re-sync the field when the committed value changes (recalc returns a new
    // residual, or the quote / program switches). No-op while typing, since the
    // committed value doesn't change until commit.
    useEffect(function syncResidualInput(){
        setResidualInput(String(committedResidual))
    }, [committedResidual])

    const commitResidual = () => {
        if (String(residualInput) === String(committedResidual)) return  // nothing changed
        setDealStateWithUpdate({ residual_override: residualInput }, true)
    }

    // Adjustment shown between the base and the total comes straight from the
    // quote (recomputed by the backend on recalc), so it stays put while typing.
    const residualAdjustment = formattedQuote.residual_adjustment ?? 0

    // Some lenders (e.g. Ally, Ford Financial) quote a lease rate instead of a
    // money factor. The backend returns a display-ready rate string (e.g.
    // "10.74%") for those banks and null otherwise, so a truthy check is enough:
    // when present, show "Rate" + the rate value; otherwise keep Money Factor.
    // Finance always quotes an APR, even on older quotes stored without a rate.
    const hasRate = dealState.is_purchase || !!formattedQuote.rate

    // ── Editable Money Factor / Rate / APR ── Edits the buy rate (the override, else
    // the program's base). The field shows the priced sell rate (buy + markup) until
    // focused, so the markup is never typed back in as an override. Like Tax Rate:
    // typing is held here and committed on Enter/blur; committing the base or a
    // blank clears the override. null = not being edited.
    const rateOverrideField = hasRate ? 'interest_rate_override' : 'money_factor_override'
    const rateOverride = dealState[rateOverrideField] || ''
    // The priced (sell) rate: buy rate plus any markup MarketScan applied. A finance
    // quote's apr is that sell rate.
    const sellRate = dealState.is_purchase ? formattedQuote.apr
        : hasRate ? parseFloat(formattedQuote.rate) : formattedQuote.money_factor
    // Quotes priced before base_rate existed carry no markup, so their sell rate is the base.
    const baseRate = formattedQuote.base_rate ?? (rateOverride ? null : sellRate)
    // Red while an override is set or the priced quote carries an applied markup.
    const isRateAdjusted = Boolean(rateOverride) || (baseRate != null && Number(sellRate) > Number(baseRate))
    const [rateDraft, setRateDraft] = useState(null)
    const [isRateFocused, setIsRateFocused] = useState(false)
    const buyRate = rateOverride || baseRate
    const shownRate = isRateFocused ? buyRate : sellRate
    const commitRate = () => {
        if (rateDraft === null) return
        const isBase = rateDraft === '' || (baseRate != null && Number(rateDraft) === Number(baseRate))
        const next = isBase ? '' : rateDraft
        setRateDraft(null)
        if (next !== rateOverride) setDealStateWithUpdate({ [rateOverrideField]: next }, true)
    }

    // ── Rate Markup ── Same commit pattern; MarketScan applies it on top of the rate.
    const rateMarkup = dealState.rate_markup || ''
    const [markupDraft, setMarkupDraft] = useState(null)
    const commitMarkup = () => {
        if (markupDraft === null) return
        setMarkupDraft(null)
        if (markupDraft !== rateMarkup) setDealStateWithUpdate({ rate_markup: markupDraft }, true)
    }

    // ── MRM exceeded ── The lender capped the MSRP it residualizes. Both values come
    // from MarketScan via the backend (null unless capped); display-only.
    const isMrmExceeded = !dealState.is_purchase
        && Number.isFinite(formattedQuote.msrp_adjusted)
        && Number.isFinite(formattedQuote.mrm_adjustment)
        && formattedQuote.mrm_adjustment < 0
    const [mrmAnchor, setMrmAnchor] = useState(null)
    // The popup's MSRP, held here until Calculate; dismissing discards it.
    const [mrmMsrpDraft, setMrmMsrpDraft] = useState('')
    const isRepricing = Boolean(updateDealResponse?.loading)
    useEffect(function closeMrmOnReprice(){
        if (isRepricing) setMrmAnchor(null)
    }, [isRepricing])
    const openMrm = (e) => {
        setMrmMsrpDraft(dealState.msrp ?? '')
        setMrmAnchor(e.currentTarget)
    }
    // Same gates as the page-level Enter (historical, desk permission, lock), which
    // updateDealFunc doesn't check itself; msrp/cost as the CALCULATE button.
    const canEditMrmMsrp = !isHistorical && canDesk() && !dealState.is_locked
    const canSubmitMrmMsrp = canEditMrmMsrp && Number(mrmMsrpDraft) > 0 && Boolean(dealState.cost) && !isRepricing
    const submitMrmMsrp = () => {
        if (!canSubmitMrmMsrp) return
        setMrmAnchor(null)
        // Re-price now, as Force Payment does: a debounced setDealStateWithUpdate(…, true)
        // never fires, since the MSRP box's onChange echo of the new value clears its flag.
        const nextState = {...dealState, msrp: mrmMsrpDraft, _updateInBackend: false}
        setDealStateWithoutUpdate(nextState)
        // A failure already rolls back (DealContext) and toasts (axios interceptor).
        updateDealFunc(nextState)?.catch(() => {})
    }

    // Enter commits the field; keep the page-level Enter from also re-pricing the old value.
    const commitOnEnter = (draft, commit) => (e) => {
        if (e.key !== 'Enter') return
        if (draft !== null) e.stopPropagation()
        commit()
    }

    // A hand-typed Cost is canonical like any other: it re-derives the
    // calculator's MSRP/Invoice relationships in the same state write. This
    // replaces an onKeyDown that cleared those columns in a separate write —
    // whether the clear survived depended on React flushing the keydown before
    // the input event, and if it lost, the old offsets snapped Cost back.
    //
    // One argument, deliberately: the second (`shouldUpdate`) opts into the
    // debounced backend re-price, which must never fire per keystroke.
    const setCost = (value) => setDealStateWithUpdate(
        costUpdate(dealState.msrp, dealState.invoice, value)
    )

    const setPaymentStateObj = (field, value, newDealState, shouldUpdate) => {
        setPaymentState({
            ...paymentState,
            [field]: value,
        })
        setDealStateWithUpdate(newDealState, shouldUpdate)
    }

    const setPaymentValueWithDealStateUpdate = (newDealState) => {
        const formattedQuote = newDealState.formatted_quote
        setPaymentState({
            desired_payment: formattedQuote.monthly_payment,
            is_drive_off_cap_reduction: newDealState.is_drive_off_cap_reduction,
            drive_off: newDealState.drive_off
        })
    }

    useEffect(function setPaymentValueWithDealStateUpdateEffect(){
        const newDealState = updateDealResponse?.data?.data
        if (newDealState && !updateDealResponse.loading && !updateDealResponse.error) {
            setPaymentValueWithDealStateUpdate(newDealState)
        }
    }, [updateDealResponse])

    if (!dealState) return null;

    const isLoading = updateDealResponse?.loading && dealState._no_update !== true
    const isPurchase = dealState.is_purchase

    // Total Cap Cost (lease) / Total Amount Financed (finance) — both read the
    // single amount_financed field the backend now includes in the Details
    // response (formatted_quote.amount_financed), derived server-side from the
    // same source the worksheet uses. Read-only display; no extra request.
    const worksheetTotalLabel = isPurchase ? 'Total Amount Financed' : 'Total Cap Cost'
    const worksheetTotal = formattedQuote?.amount_financed
    const hasWorksheetTotal = worksheetTotal !== undefined && worksheetTotal !== null && worksheetTotal !== ''

    const isCashPayment = dealState.is_purchase && dealState.is_one_pay
    const isOnePayLease = !dealState.is_purchase && dealState.is_one_pay

    // Flag a sub-780 (below-T1) applicant credit score so the input reads red.
    const creditScoreNum = Number(dealState.credit_score)
    const isLowCredit = dealState.credit_score !== '' && dealState.credit_score != null
        && !isNaN(creditScoreNum) && creditScoreNum < 780

    const cashName = paymentState.is_drive_off_cap_reduction ? 'Cap Reduction:' : 'Total Drive Off:'
    const toggleTooltipName = paymentState.is_drive_off_cap_reduction ? 'Total Drive Off' : 'Cap Reduction'
    const isUsed = !dealState.car_detail.is_new

    const rateLabel = isPurchase ? 'APR' : hasRate ? 'Rate' : 'Money Factor'
    const rateRows = (
        <React.Fragment>
            <div className={'lender-row'}>
                <span className={'lender-key'}>{rateLabel}:</span>
                <div className={classnames('lender-input-wrap', { 'has-override': isRateAdjusted })}>
                    {hasRate && <span className={'prefix'}>%</span>}
                    <CustomCurrencyInput
                        // On the native input: NumericFormat defers its own onFocus a tick.
                        inputProps={{'aria-label': rateLabel, onFocus: () => setIsRateFocused(true)}}
                        value={rateDraft ?? shownRate}
                        disabled={isHistorical}
                        allowBlank={true}
                        allowNegative={false}
                        thousandSeparator={false}
                        decimalScale={hasRate ? 4 : 7}
                        onChange={(value, sourceInfo) => {
                            if (sourceInfo?.source === 'event') setRateDraft(value)
                        }}
                        onBlur={() => {
                            setIsRateFocused(false)
                            commitRate()
                        }}
                        onKeyDown={commitOnEnter(rateDraft, commitRate)}
                        InputProps={{}}
                    />
                </div>
            </div>
            <div className={'lender-row'}>
                <span className={'lender-key'}>Rate Markup:</span>
                <div className={'lender-input-wrap'}>
                    <CustomCurrencyInput
                        inputProps={{'aria-label': 'Rate Markup'}}
                        value={markupDraft ?? rateMarkup}
                        placeholder={isPurchase ? '0' : undefined}
                        disabled={isHistorical}
                        allowBlank={true}
                        allowNegative={false}
                        thousandSeparator={false}
                        decimalScale={hasRate ? 4 : 7}
                        onChange={(value, sourceInfo) => {
                            if (sourceInfo?.source === 'event') setMarkupDraft(value)
                        }}
                        onBlur={commitMarkup}
                        onKeyDown={commitOnEnter(markupDraft, commitMarkup)}
                        InputProps={{}}
                    />
                </div>
            </div>
        </React.Fragment>
    )

    return (
        <Stack
            direction={isTabletOrMobile ? 'column' : 'row'}
            justifyContent="space-between"
            alignItems="center"
            className={'dealPagePayment'}
        >
            <Box className={classnames("dealPagePayment-column leftColumn panel-pricing", {
                mobile: isTabletOrMobile
            })}>
                <div className={'panel-header'}>
                    <div className={'panel-title'}>Vehicle Pricing</div>
                    {/*<LocalOfferOutlinedIcon/>*/}
                </div>
                <div className={'panel-body'}>
                {/* MSRP */}
                <div className={'pricing-row'}>
                    <span className={'pricing-label'}>MSRP:</span>
                    {
                        isMrmExceeded &&
                        <button
                            type={'button'}
                            className={'mrm-warning'}
                            aria-label={'Warning, MRM exceeded'}
                            aria-haspopup={'dialog'}
                            onClick={openMrm}
                            // Opening must not also re-run Calculate.
                            onKeyDown={(e) => e.key === 'Enter' && e.stopPropagation()}
                        >
                            <ErrorOutlineIcon/>
                        </button>
                    }
                    <div className={classnames('pricing-input-wrap', { 'mrm-exceeded': isMrmExceeded })}>
                        <span className={'currency-prefix'}>$</span>
                        <CustomCurrencyInput
                            className={classnames({ loading: isLoading })}
                            placeholder={'0'}
                            value={dealState.msrp}
                            disabled={isHistorical}
                            onChange={(value) => setDealStateWithUpdate({msrp: value})}
                            InputProps={{}}
                        />
                    </div>
                </div>

                {/* Invoice */}
                <div className={'pricing-row'}>
                    <span className={'pricing-label'}>Invoice:</span>
                    <div className={'pricing-input-wrap'}>
                        <span className={'currency-prefix'}>$</span>
                        <CustomCurrencyInput
                            className={classnames({ loading: isLoading })}
                            placeholder={'0'}
                            value={dealState.invoice}
                            disabled={isHistorical}
                            onChange={(value) => setDealStateWithUpdate({invoice: value})}
                            InputProps={{}}
                        />
                    </div>
                </div>

                {/* Cost */}
                <div className={'pricing-row'}>
                    <span className={'pricing-label'}>
                        Cost:
                        <CostCalculator
                            state={dealState}
                            setState={setDealStateWithUpdate}
                            isLoading={isLoading}
                            disabled={isHistorical}
                        />
                    </span>
                    <div className={'pricing-input-wrap'}>
                        <span className={'currency-prefix'}>$</span>
                        <CustomCurrencyInput
                            className={classnames({ loading: isLoading })}
                            placeholder={'0'}
                            value={dealState.cost}
                            disabled={isHistorical}
                            onChange={setCost}
                            InputProps={{}}
                        />
                    </div>
                </div>

                {/* Profit */}
                <div className={'pricing-row profit-row'}>
                    <span className={'pricing-label'}>Profit:</span>
                    <div className={'pricing-input-wrap'}>
                        <span className={'currency-prefix'}>$</span>
                        <CustomCurrencyInput
                            className={classnames({ loading: isLoading })}
                            placeholder={'0'}
                            value={dealState.profit}
                            disabled={isHistorical}
                            onChange={(value) => setDealStateWithUpdate({profit: value})}
                            InputProps={{}}
                        />
                    </div>
                </div>

                {/* Selling Price — read-only, computed */}
                <div className={'pricing-row'}>
                    <span className={'pricing-label'}>Selling Price:</span>
                    <span className={'selling-price-value'}>
                        <span className={'selling-dollar'}>$</span>
                        <span style={{ flex: 1, textAlign: 'right' }}>
                            <NumericFormat
                                decimalScale={2}
                                value={dealState.selling_price || (Number(dealState.cost) + Number(dealState.profit))}
                                displayType={'text'}
                                thousandSeparator
                            />
                        </span>
                    </span>
                </div>

                {/* Total Cap Cost (lease) / Total Amount Financed (finance) —
                    read-only, straight from the Details response. Same styling
                    as the Selling Price row above. */}
                <div className={'pricing-row'}>
                    <span className={'pricing-label'}>{worksheetTotalLabel}:</span>
                    <span className={'selling-price-value'}>
                        <span className={'selling-dollar'}>$</span>
                        <span style={{ flex: 1, textAlign: 'right' }}>
                            {hasWorksheetTotal && (
                                <NumericFormat
                                    decimalScale={2}
                                    fixedDecimalScale
                                    value={worksheetTotal}
                                    displayType={'text'}
                                    thousandSeparator
                                />
                            )}
                        </span>
                    </span>
                </div>
                </div>{/* end .panel-body */}
                {
                    // The Amount Due at Signing breakdown card, plus a Force Payment-style
                    // MSRP box and Calculate. Escape / click away discard the draft.
                    isMrmExceeded && mrmAnchor &&
                    <Popover
                        open={true}
                        anchorEl={mrmAnchor}
                        onClose={() => setMrmAnchor(null)}
                        anchorOrigin={{vertical: 'bottom', horizontal: 'left'}}
                        transformOrigin={{vertical: 'top', horizontal: 'left'}}
                        slotProps={{paper: {className: 'AmountDueBreakdownPopover-paper'}}}
                        onKeyDown={(e) => {
                            if (e.key !== 'Enter') return
                            // Keep the page-level Enter from re-running Calculate.
                            e.stopPropagation()
                            // A focused button activates itself.
                            if (e.target.tagName === 'BUTTON') return
                            e.preventDefault()
                            submitMrmMsrp()
                        }}
                    >
                        <div className={'AmountDueBreakdownPopover MrmPopover'} data-testid={'mrm-popover'}>
                            <div className={'adb-header'}>
                                <ErrorOutlineIcon/>
                                <h2>Warning, MRM exceeded</h2>
                            </div>
                            <div className={'adb-body'}>
                                <div className={'adb-row mrm-msrp-row'}>
                                    <label className={'adb-label'} htmlFor={'mrm-msrp'}>MSRP</label>
                                    <div className={classnames('mrm-msrp-input', { 'is-disabled': !canEditMrmMsrp })}>
                                        <span className={'currency-prefix'}>$</span>
                                        <CustomCurrencyInput
                                            id={'mrm-msrp'}
                                            value={mrmMsrpDraft}
                                            disabled={!canEditMrmMsrp}
                                            allowBlank={true}
                                            allowNegative={false}
                                            autoFocus={canEditMrmMsrp}
                                            onFocus={(e) => e.target.select()}
                                            autoComplete={'off'}
                                            onChange={(value) => setMrmMsrpDraft(value)}
                                            InputProps={{}}
                                        />
                                    </div>
                                </div>
                                <div className={'adb-row'}>
                                    <span className={'adb-label'}>Maximum Residualized MSRP</span>
                                    <span className={'adb-value'}>{formattedQuote.msrp_adjusted.toDollarFormat()}</span>
                                </div>
                                <div className={'adb-row'}>
                                    <span className={'adb-label'}>MSRP Adjustment</span>
                                    <span className={'adb-value'}>{formattedQuote.mrm_adjustment.toDollarFormat()}</span>
                                </div>
                            </div>
                            {
                                canEditMrmMsrp &&
                                <div className={'mrm-footer'}>
                                    <button type={'button'} className={'mrm-submit'} onClick={submitMrmMsrp} disabled={!canSubmitMrmMsrp}>
                                        Calculate
                                    </button>
                                </div>
                            }
                        </div>
                    </Popover>
                }
            </Box>

            <Box className={classnames("dealPagePayment-column rightColumn panel-lender", {
                mobile: isTabletOrMobile
            })}>
                {
                    dealState.id &&
                    <React.Fragment>
                        <div className={'panel-header'}>
                            <div className={'panel-title'}>{isPurchase ? 'Lender Details' : 'Program Details'}</div>
                            {
                                // Opens the grid, which scans every lender — the
                                // single most expensive desking action there is.
                                canDesk() &&
                            <Tooltip title={isHistorical ? '' : 'All Quotes'} arrow placement={'top'}>
                                <button
                                    type="button"
                                    className={'panel-header-edit'}
                                    disabled={isHistorical}
                                    onClick={() => {
                                        openModal({
                                            element: DeskingModal,
                                            id: "DeskingModal",
                                            props: {
                                                onSave: (updateData) => {
                                                    setDealStateWithUpdate(updateData, false)
                                                    setPaymentValueWithDealStateUpdate(updateData)
                                                },
                                                ...dealState,
                                                dealState: dealState,
                                                id: dealState.id,
                                                deal_id: dealState.id,
                                                selectedLender: dealState.formatted_quote.lender_name,
                                                selectedTerm: dealState.term,
                                                // program_code pins the grid highlight to the deal's
                                                // exact selected program (term/lender/tier alone
                                                // over-match). Tier kept as a fallback.
                                                selectedProgramCode: dealState.formatted_quote.program_code ?? dealState.program_code,
                                                selectedTier: dealState.formatted_quote.tier ?? dealState.tier,
                                                desired_payment: null,
                                            }
                                        })
                                    }}
                                >
                                    <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                                        <polyline points="14 2 14 8 20 8"/>
                                        <line x1="16" y1="13" x2="8" y2="13"/>
                                        <line x1="16" y1="17" x2="8" y2="17"/>
                                        <polyline points="10 9 9 9 8 9"/>
                                    </svg>
                                </button>
                            </Tooltip>
                            }
                        </div>
                        <div className={'panel-body'}>
                            <div className={'lender-name'}>
                                {formattedQuote.lender_name}
                                <Tooltip title={formattedQuote.program_description} arrow placement={'top'}>
                                    <span className={'info-icon'}>i</span>
                                </Tooltip>
                            </div>
                            <div className={'lender-row'}>
                                <span className={'lender-key'}>Applicant Credit Score</span>
                                <div className={classnames('lender-input-wrap', { 'low-credit': isLowCredit })}>
                                    <input
                                        type="text"
                                        value={dealState.credit_score || null}
                                        readOnly={isHistorical}
                                        onChange={(e) => setDealStateWithUpdate({credit_score: e.target.value})}
                                    />
                                </div>
                            </div>

                            {
                                !isPurchase &&
                                <React.Fragment>
                                    {rateRows}
                                    <div className={'lender-row'}>
                                        <span className={'lender-key'}>Residual:</span>
                                        {/* Always [base input] + [adjustment] = [final]% per the design. */}
                                        <div className={'residual-formula'}>
                                            <input
                                                className={classnames('residual-input', { 'has-override': hasResidualOverride })}
                                                type="text"
                                                maxLength={2}
                                                value={residualInput}
                                                readOnly={isHistorical}
                                                onChange={(e) => setResidualInput(e.target.value)}
                                                onKeyDown={(e) => { if (e.key === 'Enter') commitResidual() }}
                                                onBlur={commitResidual}
                                            />
                                            <span className={'residual-rest'}>
                                                <span className={'residual-op'}>+</span>
                                                <span className={'residual-num'}>{residualAdjustment}</span>
                                                <span className={'residual-op'}>=</span>
                                                <span className={'residual-num'}>{formattedQuote.residual}%</span>
                                            </span>
                                        </div>
                                    </div>
                                    {/* Read-only residual dollar amount (lease only),
                                        shown as whole dollars. */}
                                    <div className={'lender-row'}>
                                        <span className={'lender-key'}>Residual Amount:</span>
                                        <span className={'lender-val-plain'}>
                                            <span className={'dollar'}>$</span>
                                            <span className={'amount'}>
                                                <NumericFormat
                                                    value={formattedQuote.residual_value || 0}
                                                    displayType={'text'}
                                                    thousandSeparator={true}
                                                    decimalScale={0}
                                                />
                                            </span>
                                        </span>
                                    </div>
                                </React.Fragment>
                            }
                            {
                                isCashPayment &&
                                <React.Fragment>
                                    <div className={'lender-row'}>
                                        <span className={'lender-key'}>APR:</span>
                                        <span className={'lender-val-plain'}>N/A</span>
                                    </div>
                                    <div className={'lender-row'}>
                                        <span className={'lender-key'}>Rate Markup:</span>
                                        <span className={'lender-val-plain'}>N/A</span>
                                    </div>
                                </React.Fragment>
                            }
                            {
                                isPurchase && !isCashPayment && rateRows
                            }
                        </div>
                    </React.Fragment>
                }
            </Box>
        </Stack>
    )
}